import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";

import { api } from "../api/client";
import { Topic, useChanged } from "./changes";
import { queryClient } from "./queryClient";

interface UsePagedApiResult<T> {
  data: T[] | null;
  loading: boolean;
  error: boolean;
  reload: () => void;
  mutate: Dispatch<SetStateAction<T[] | null>>;
  // Whether the API may have more past what's loaded.
  hasMore: boolean;
  loadingMore: boolean;
  // The last page failed; `more` tries it again.
  moreError: boolean;
  more: () => void;
  // Render below the list while `hasMore`: the next page loads as it nears
  // the viewport.
  sentinelRef: (el: HTMLElement | null) => void;
}

// Where a list's first page is remembered, in `useApi`'s cache (ADR 0055).
const firstPage = (auth: boolean, path: string) => ["paged", auth, path];

// The first page a list showed for `path`, if it's remembered.
const remembered = <T>(auth: boolean, path: string | null): T[] | undefined =>
  path === null
    ? undefined
    : queryClient.getQueryData<T[]>(firstPage(auth, path));

// `path` asking for `limit` items from `offset`.
const pageOf = (path: string, limit: number, offset: number) =>
  `${path}${path.includes("?") ? "&" : "?"}${new URLSearchParams({
    limit: String(limit),
    offset: String(offset),
  })}`;

/**
 * A list the API serves a page at a time (`limit` and `offset`), loaded as
 * it's scrolled: like `useApi`, the first page loads (after `debounce`) for
 * each `path` and the last results stay while it does; then each time the
 * sentinel nears the viewport the next page is appended, until one comes
 * back short. Items already shown (by `keyOf`) are skipped, as the list can
 * shift between pages. Without IntersectionObserver only the first page
 * shows. The first page is remembered as `useApi`'s answers are, so a list
 * that comes back shows it at once and refreshes it behind.
 */
export function usePagedApi<T>(
  path: string | null,
  options: {
    pageSize: number;
    keyOf: (item: T) => string | number;
    auth?: boolean;
    debounce?: number;
    // What of yours it shows, so a change to it reloads it.
    about?: Topic[];
  },
): UsePagedApiResult<T> {
  const { pageSize, keyOf } = options;
  const auth = options.auth ?? true;
  const debounce = options.debounce ?? 0;
  const [data, setData] = useState<T[] | null>(
    () => remembered<T>(auth, path) ?? null,
  );
  const [loading, setLoading] = useState(
    path !== null && remembered(auth, path) === undefined,
  );
  const [error, setError] = useState(false);
  const [tick, setTick] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [moreError, setMoreError] = useState(false);
  const [sentinel, setSentinel] = useState<HTMLElement | null>(null);
  // The path whose pages are showing, and how many items the API has served
  // for it (an item added locally through `mutate` doesn't move the offset).
  const shown = useRef<string | null>(null);
  const offset = useRef(0);
  const keyOfRef = useRef(keyOf);
  keyOfRef.current = keyOf;

  const reload = useCallback(() => setTick((t) => t + 1), []);
  useChanged(options.about, reload);

  useEffect(() => {
    shown.current = null;
    setHasMore(false);
    setMoreError(false);
    if (path === null) {
      setData(null);
      setLoading(false);
      setError(false);
      return;
    }

    let stale = false;
    const kept = remembered<T>(auth, path);
    if (kept !== undefined) {
      shown.current = path;
      offset.current = kept.length;
      setData(kept);
      setHasMore(kept.length >= pageSize);
    }
    setLoading(kept === undefined);
    setError(false);

    const run = async () => {
      try {
        const page = await api.get<T[]>(pageOf(path, pageSize, 0), auth);
        queryClient.setQueryData(firstPage(auth, path), page);
        if (stale) return;
        shown.current = path;
        offset.current = page.length;
        setData(page);
        setHasMore(page.length >= pageSize);
      } catch {
        if (!stale) setError(true);
      }
      if (!stale) setLoading(false);
    };

    const handle = setTimeout(run, debounce);
    return () => {
      clearTimeout(handle);
      stale = true;
    };
  }, [path, auth, debounce, pageSize, tick]);

  const more = useCallback(async () => {
    const from = shown.current;
    if (from === null || from !== path || loadingMore) return;
    setLoadingMore(true);
    setMoreError(false);
    try {
      const page = await api.get<T[]>(
        pageOf(from, pageSize, offset.current),
        auth,
      );
      if (shown.current !== from) return;
      offset.current += page.length;
      setData((prev) => {
        const seen = new Set((prev ?? []).map(keyOfRef.current));
        return [
          ...(prev ?? []),
          ...page.filter((item) => !seen.has(keyOfRef.current(item))),
        ];
      });
      setHasMore(page.length >= pageSize);
    } catch {
      if (shown.current === from) setMoreError(true);
    } finally {
      setLoadingMore(false);
    }
  }, [path, pageSize, auth, loadingMore]);

  // Re-observe after each page: a fresh observer reports the sentinel's
  // current state, so one still on screen keeps loading until the viewport
  // is filled or the list runs out. A failed page waits for `more`.
  const ready = hasMore && !loading && !loadingMore && !moreError;
  useEffect(() => {
    if (!sentinel || !ready || typeof IntersectionObserver === "undefined")
      return;
    const observer = new IntersectionObserver(
      (items) => {
        if (items[0].isIntersecting) more();
      },
      { rootMargin: "400px" },
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [sentinel, ready, more, data]);

  const sentinelRef = useCallback((el: HTMLElement | null) => {
    setSentinel(el);
  }, []);

  return {
    data,
    loading,
    error,
    reload,
    mutate: setData,
    hasMore,
    loadingMore,
    moreError,
    more,
    sentinelRef,
  };
}
